import { Box } from '@moonpig/launchpad-components'
import React, { FC, PropsWithChildren, useEffect, useState } from 'react'
import { styled, breakpoint } from '@moonpig/launchpad-utils'
import { useInView } from 'react-intersection-observer'
import { useExperimentString } from '@moonpig/web-core-eppo/browser'
import { useHasScrolled, useIsMobile } from '../../../utils'
import { HEADER_HEIGHT_PX } from '../constants'

const StyledFiltersCarouselBox = styled(Box)<{
  isSticky: boolean
  useDropdownFilters: boolean
}>`
  position: ${({ isSticky, useDropdownFilters }) =>
    isSticky || useDropdownFilters ? 'sticky' : 'static'};
  top: -1px;
  z-index: 500;

  ${breakpoint('md')} {
    top: 0px;
  }

  ${breakpoint('xl')} {
    top: ${({ useDropdownFilters }) =>
      useDropdownFilters ? '0px' : HEADER_HEIGHT_PX};
  }
`

export const FiltersCarouselStickyWrapper: FC<
  PropsWithChildren<{ onStickyChange: (sticky: boolean) => void }>
> = ({ children, onStickyChange }) => {
  const isMobile = useIsMobile()
  const [isSticky, setIsSticky] = useState(false)
  const useDropdownFilters =
    useExperimentString('search-use-dropdown-filters-v2', {
      fallback: 'disabled',
    })?.toLocaleLowerCase() === 'enabled'
  const [filtersCarouselRef, inView] = useInView({
    threshold: 1,
  })
  const hasScrolled = useHasScrolled()

  useEffect(() => {
    if (hasScrolled && useDropdownFilters) {
      onStickyChange(!inView)
    }
  }, [onStickyChange, inView, hasScrolled, useDropdownFilters])

  useEffect(() => {
    let prevScrollDepth = window.scrollY
    let thresholdLimitUp = window.scrollY
    let thresholdLimitDown = window.scrollY

    const scrollEvent = () => {
      const currentScrollDepth = window.scrollY
      if (
        prevScrollDepth > currentScrollDepth ||
        (currentScrollDepth > 300 && isMobile)
      ) {
        thresholdLimitDown = currentScrollDepth

        if (
          thresholdLimitUp - 70 > currentScrollDepth &&
          (currentScrollDepth > 300 || isMobile)
        ) {
          setIsSticky(true)
          onStickyChange(true)
          thresholdLimitUp = currentScrollDepth
        }
      }

      if (
        prevScrollDepth < currentScrollDepth ||
        isSticky ||
        (currentScrollDepth < 300 && !isMobile)
      ) {
        thresholdLimitUp = currentScrollDepth

        /* istanbul ignore next */
        if (
          thresholdLimitDown < currentScrollDepth ||
          (currentScrollDepth < 300 && !isMobile)
        ) {
          thresholdLimitDown = currentScrollDepth
          setIsSticky(false)
          onStickyChange(false)
        }
      }

      prevScrollDepth = currentScrollDepth
    }

    if (!useDropdownFilters) {
      window.addEventListener('scroll', scrollEvent)
    }

    return () => window.removeEventListener('scroll', scrollEvent)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useDropdownFilters])

  return (
    <>
      <div
        ref={filtersCarouselRef}
        style={{ height: 1 }}
        data-testid={'sticky-filters-carousel-sentinel'}
      />
      <StyledFiltersCarouselBox
        data-testid={'sticky-filters-carousel'}
        isSticky={isSticky}
        useDropdownFilters={useDropdownFilters}
      >
        {children}
      </StyledFiltersCarouselBox>
    </>
  )
}
